import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type RootState from 'app-components/utils/redux-store/rootState';

import { resetPreferenceOverride, updatePreferenceOverride } from './preferencesV2Actions';
import type { PreferenceV2Name, PreferenceV2Value } from './preferencesV2Names';
import { getPreferenceObj, getPreferenceValue, type PreferenceV2ValueWithDefault } from './preferencesV2Selectors';

export type PreferenceV2UndoOptions = {
  userMessage?: string;
  disableUndoAction?: boolean;
};

/** A union of names only takes a value that every one of them can hold. */
export type PreferenceV2WriteValue<Name extends PreferenceV2Name> = {
  [Each in Name]: (value: PreferenceV2Value<Each>) => void;
}[Name] extends (value: infer Value) => void
  ? Value
  : never;

export type PreferenceV2Setter<Name extends PreferenceV2Name> = (
  value: PreferenceV2WriteValue<Name>,
  undoOptions?: PreferenceV2UndoOptions,
) => void;

// note - Please use this hook whenever using preferences v2. You might confuse yourself if you do not. See some of the comments below
// NoInfer stops an annotated result from inferring a default that the call never passed.
export const usePreferenceV2 = <
  Name extends PreferenceV2Name,
  Default extends PreferenceV2Value<Name> | undefined = undefined,
>(
  preferenceName: Name,
  defaultValue?: Default,
): readonly [PreferenceV2ValueWithDefault<Name, NoInfer<Default>>, PreferenceV2Setter<Name>, () => void] => {
  const dispatch = useDispatch();
  const preference = useSelector((state: RootState) => getPreferenceObj(state, preferenceName));
  const preferenceValue = useSelector((state: RootState) => getPreferenceValue(state, preferenceName, defaultValue));

  // updatePreference has id as the name in order to do an optimistic update. We want to key off of preference name bc it is stable
  const updatePreference = useCallback(
    (value: PreferenceV2WriteValue<Name>, undoOptions?: PreferenceV2UndoOptions) => {
      dispatch(
        updatePreferenceOverride(
          {
            preferenceName,
            value,
            preferenceId: preference?.preferenceId,
            id: preferenceName,
            dataType: preference?.dataType,
          },
          undoOptions && { undo: undoOptions },
        ),
      );
    },
    [dispatch, preferenceName, preference?.preferenceId, preference?.dataType],
  );

  // resetPreference resets the preference to the default value and removes any overrides.
  const resetPreference = useCallback(
    () => dispatch(resetPreferenceOverride({ id: preferenceName })),
    [dispatch, preferenceName],
  );

  return [preferenceValue, updatePreference, resetPreference];
};

export const useUpdatePreferenceV2 = <Name extends PreferenceV2Name>(
  preferenceName: Name,
): readonly [PreferenceV2Setter<Name>] => {
  const dispatch = useDispatch();
  const preference = useSelector((state: RootState) => getPreferenceObj(state, preferenceName));

  // updatePreference has id as the name in order to do an optimistic update. We want to key off of preference name bc it is stable
  const updatePreference = useCallback(
    (value: PreferenceV2WriteValue<Name>, undoOptions?: PreferenceV2UndoOptions) =>
      dispatch(
        updatePreferenceOverride(
          {
            preferenceName,
            value,
            preferenceId: preference?.preferenceId,
            id: preferenceName,
            dataType: preference?.dataType,
          },
          undoOptions && { undo: undoOptions },
        ),
      ),
    [dispatch, preferenceName, preference?.preferenceId, preference?.dataType],
  );

  return [updatePreference];
};

export default usePreferenceV2;
